// @flow

import React, { useEffect, useState, useRef, Suspense } from "react";
import WeeklyRealizationShift from "./WeeklyRealizationShift";
import ProductionModal from "./ProductionModal";
import type { ProductionModalParams, MicroplanSummaryFilterProps } from "./WeeklyRealizationCommon";
import Loader from "../Loader";
import { loadRealizationWithPlan } from "../../lib/WeeklyRealization";
import * as t from "../../lib/backend/manufacturing2.generated.types";
import type { WeeklyReport } from "../../lib/Models";
import * as BusinessLogic from "../../lib/BusinessLogic";
import { renderToString } from "react-dom/server";
import { exportWeeklyRealizationHTML } from "./WeeklyRealizationExport";
import { getBackend } from "../../lib/backend/Backend2";

import type { ScrapUptimeMatrixLines } from "../manufacturing/WeeklyRealizationCommon";

import "./WeeklyRealization.scss";

const ExportToXLSX = React.lazy(() => import("./ExportToXLSX"));

type State = {
    show: boolean,
    productionModalParams?: ProductionModalParams,
};

type Props = {
    plant_uuid: string,
    line_group_uuid: string,
    filter: MicroplanSummaryFilterProps,
    exportToXLSXButtonSetLoadingState: Function,
    breakdown_level: string,
    is_export: boolean,
    is_print?: boolean,
    state?: State,
    scrap_uptime_matrix?: ScrapUptimeMatrixLines,
    loaded: boolean,
    merged_data: WeeklyReport[],
    insights: t.IEventDataEx[][],
};

const initialState: State = {
    show: false,
    productionModalParams: undefined,
};

export const loadWeeklyRealization = async (
    week: number,
    year: number,
    plant_uuid: string,
    line_group_uuid: string,
    sim_report_add_to_cache: boolean
): Promise<WeeklyReport[] | null> => {
    const line_group = BusinessLogic.getLineGroupForUser(line_group_uuid);
    const line_uuids = line_group ? line_group.line_uuids : [];

    if (line_uuids.length === 0) {
        return [];
    }

    try {
        // go to the backend and get realization
        const result = await loadRealizationWithPlan(
            week,
            year,
            plant_uuid,
            line_uuids,
            false,
            sim_report_add_to_cache
        );

        if (result.error) {
            console.error(result.error);
            return null;
        }

        const lines = await getBackend().manufacturing.getLines({
            line_uuids: line_uuids,
        });

        const remove_lines_uuid = lines.lines
            .filter(el => el.tags.skip_weekly_realization === "true")
            .map(el => el.uuid);

        if (remove_lines_uuid.length > 0) {
            result.data = result.data.filter(el => !remove_lines_uuid.includes(el.line_uuid));
        }

        return result.data;
    } catch (error) {
        console.error(error);
        return null;
    }
};

const exportWeeklyRealization = (props: Props, state: State) => () => {
    exportWeeklyRealizationHTML(renderToString(<WeeklyRealization {...props} is_print={true} state={state} />));
};

const WeeklyRealization = (props: Props) => {
    const [state: State, setState] = useState(initialState);

    const ref = useRef(null);
    const onShowProductionModal = data =>
        setState(prevState => {
            return { ...prevState, ...data };
        });

    // used for printing
    if (props.state && JSON.stringify(props.state) !== JSON.stringify(state)) {
        setState({ ...props.state });
    }

    useEffect(() => {
        const { current } = ref;
        if (props.loaded && current !== null) {
            current.removeEventListener("export-weekly-realization-for-print", exportWeeklyRealization(props, state));
            current.addEventListener("export-weekly-realization-for-print", exportWeeklyRealization(props, state));
        }
    }, [props.loaded]);

    if (!props.loaded) {
        return <Loader />;
    }

    const ExportComp = (
        <ExportToXLSX
            exportToXLSXButtonSetLoadingState={props.exportToXLSXButtonSetLoadingState}
            loading={!props.loaded}
        />
    );

    return (
        <div id="weekly-realization" ref={ref}>
            {state.productionModalParams && (
                <ProductionModal
                    show={state.show}
                    data={state.productionModalParams}
                    onShowProductionModal={onShowProductionModal}
                />
            )}

            <WeeklyRealizationShift
                is_print={props.is_print}
                is_export={false}
                insights={props.insights}
                onShowProductionModal={onShowProductionModal}
                merged_data={props.merged_data}
                filter={props.filter}
                line_group_uuid={props.line_group_uuid}
                scrap_uptime_matrix={props.scrap_uptime_matrix}
            />

            {props.is_print ? ExportComp : <Suspense fallback={<Loader />}> {ExportComp} </Suspense>}
        </div>
    );
};

export default WeeklyRealization;
